import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useOrders } from '../../contexts/OrdersContext';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import type { AdminOrder, AdminOrderStatus } from '../../mocks/adminOrders';

type Tab = 'orders' | 'profile';

const statusConfig: Record<AdminOrderStatus, { label: string; color: string; icon: string }> = {
  payment_pending: { label: 'Awaiting Confirmation', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: 'ri-time-line' },
  pending:         { label: 'Confirmed', color: 'text-sky-700 bg-sky-50 border-sky-200', icon: 'ri-checkbox-circle-line' },
  processing:      { label: 'Processing', color: 'text-[#FF6B35] bg-orange-50 border-orange-200', icon: 'ri-loader-4-line' },
  on_way:          { label: 'On the Way', color: 'text-indigo-700 bg-indigo-50 border-indigo-200', icon: 'ri-motorbike-line' },
  delivered:       { label: 'Delivered', color: 'text-green-700 bg-green-50 border-green-200', icon: 'ri-checkbox-circle-fill' },
  cancelled:       { label: 'Cancelled', color: 'text-red-600 bg-red-50 border-red-200', icon: 'ri-close-circle-line' },
};

function CancelConfirmModal({ orderId, onConfirm, onClose }: { orderId: string; onConfirm: () => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-white rounded-2xl p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-500 mx-auto mb-4">
          <i className="ri-close-circle-line text-3xl" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 text-center mb-1">Cancel Order?</h3>
        <p className="text-sm text-gray-500 text-center mb-1">Order #{orderId}</p>
        <p className="text-sm text-gray-500 text-center mb-6">
          This action cannot be undone. The order will be marked as cancelled.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-700 font-medium py-3 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Keep Order
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, onCancelRequest }: { order: AdminOrder; onCancelRequest: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = statusConfig[order.status];
  const date = new Date(order.createdAt).toLocaleDateString('en-GY', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = new Date(order.createdAt).toLocaleTimeString('en-GY', { hour: '2-digit', minute: '2-digit' });
  const isActive = !['delivered', 'cancelled'].includes(order.status);

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-[#fafaf8] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35]">
            <i className="ri-shopping-bag-3-line text-lg" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Order #{order.id}</p>
            <p className="text-xs text-gray-400 mt-0.5">{date} &middot; {time}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActive && (
            <Link
              to={`/order/${order.id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#FF6B35] bg-[#FFF5F0] border border-orange-200 px-3 py-1.5 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-map-pin-time-line" />
              Track
            </Link>
          )}
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.color} whitespace-nowrap`}>
            <i className={`${cfg.icon} mr-1`} />
            {cfg.label}
          </span>
          <span className="text-sm font-bold text-gray-900 whitespace-nowrap">GYD {order.total.toLocaleString()}</span>
          <div className="w-5 h-5 flex items-center justify-center text-gray-400">
            <i className={`text-base ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 bg-[#fafaf8]">
          {/* Status tracker */}
          <div className="flex items-center gap-1 mb-5 overflow-x-auto pb-1">
            {(['payment_pending', 'pending', 'processing', 'on_way', 'delivered'] as AdminOrderStatus[]).map((s, i, arr) => {
              const isCancelled = order.status === 'cancelled';
              const statuses = arr;
              const currentIdx = statuses.indexOf(order.status as AdminOrderStatus);
              const stepIdx = statuses.indexOf(s);
              const isDone = !isCancelled && stepIdx <= currentIdx;
              const isCurrent = !isCancelled && stepIdx === currentIdx;
              const stepCfg = statusConfig[s];
              return (
                <div key={s} className="flex items-center gap-1 flex-shrink-0">
                  <div className={`flex flex-col items-center gap-1 ${isCurrent ? 'opacity-100' : isDone ? 'opacity-100' : 'opacity-40'}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${isCurrent ? 'bg-[#FF6B35] text-white' : isDone ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                      {isDone && !isCurrent ? <i className="ri-check-line text-xs" /> : <i className={`${stepCfg.icon} text-xs`} />}
                    </div>
                    <span className={`text-[9px] font-medium whitespace-nowrap ${isCurrent ? 'text-[#FF6B35]' : 'text-gray-400'}`}>{stepCfg.label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div className={`w-6 h-0.5 flex-shrink-0 mb-4 ${!isCancelled && stepIdx < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
            {order.status === 'cancelled' && (
              <div className="ml-2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 border border-red-200">
                <i className="ri-close-circle-line text-red-500 text-xs" />
                <span className="text-xs text-red-600 font-medium">Cancelled</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-400 mb-1">Items ordered</p>
              <div className="space-y-1">
                {order.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{item.qty}× {item.name}</span>
                    <span className="text-sm text-gray-500">GYD {item.price.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Delivered to</p>
                <p className="text-sm text-gray-700">{order.address}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Payment</p>
                <p className="text-sm text-gray-700">{order.paymentMethod}</p>
              </div>
              {order.rider && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Rider</p>
                  <p className="text-sm text-gray-700 flex items-center gap-1">
                    <i className="ri-motorbike-line text-[#FF6B35]" />
                    {order.rider}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-3">
            {isActive && (
              <Link
                to={`/order/${order.id}`}
                className="flex items-center gap-2 text-sm text-[#FF6B35] hover:text-white font-medium transition-colors cursor-pointer whitespace-nowrap border border-orange-200 hover:border-[#FF6B35] hover:bg-[#FF6B35] rounded-lg px-4 py-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-time-line text-base" />
                </div>
                Track Order
              </Link>
            )}
            {order.status === 'payment_pending' && (
              <button
                onClick={(e) => { e.stopPropagation(); onCancelRequest(order.id); }}
                className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600 font-medium transition-colors cursor-pointer whitespace-nowrap border border-red-200 hover:border-red-300 hover:bg-red-50 rounded-lg px-4 py-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-close-circle-line text-base" />
                </div>
                Cancel Order
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AccountPage() {
  const { user, logout, updateProfile, isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { getOrdersByUser, cancelOrder } = useOrders();
  const [tab, setTab] = useState<Tab>('orders');
  const [editMode, setEditMode] = useState(false);
  const [formName, setFormName] = useState(user?.name || '');
  const [formPhone, setFormPhone] = useState(user?.phone || '');
  const [formAddress, setFormAddress] = useState(user?.address || '');
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  const orders = user?.id ? getOrdersByUser(user.id) : [];

  const handleCancelConfirm = () => {
    if (!cancelTargetId) return;
    const success = cancelOrder(cancelTargetId);
    if (success) {
      toast.success('Order cancelled', `Order #${cancelTargetId} has been cancelled successfully.`);
    } else {
      toast.error('Cannot cancel', 'This order can no longer be cancelled.');
    }
    setCancelTargetId(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#fafaf8] flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 pt-24 pb-16">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] mx-auto mb-6">
              <i className="ri-user-lock-line text-4xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.04em' }}>Sign In Required</h2>
            <p className="text-gray-500 text-sm mb-8">You need to be signed in to view your account and order history.</p>
            <div className="flex flex-col gap-3">
              <Link to="/auth/login" className="bg-[#FF6B35] text-white font-semibold py-3 rounded-lg text-sm text-center hover:bg-[#e85d2a] transition-colors cursor-pointer whitespace-nowrap">Sign In</Link>
              <Link to="/auth/register" className="border border-gray-200 text-gray-700 font-medium py-3 rounded-lg text-sm text-center hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">Create Account</Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleSaveProfile = () => {
    updateProfile({ name: formName, phone: formPhone, address: formAddress });
    setEditMode(false);
    toast.success('Profile updated!', 'Your personal information has been saved.');
  };

  const handleLogout = () => {
    toast.info('Signed out', 'See you next time!');
    logout();
    navigate('/');
  };

  const deliveredOrders = orders.filter(o => o.status === 'delivered').length;
  const totalSpent = orders.filter(o => o.status === 'delivered').reduce((s, o) => s + o.total, 0);
  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status)).length;

  return (
    <div className="min-h-screen bg-[#fafaf8] flex flex-col">
      <Navbar />

      {cancelTargetId && (
        <CancelConfirmModal
          orderId={cancelTargetId}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelTargetId(null)}
        />
      )}

      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B35] to-[#e85d2a] text-white text-2xl font-bold select-none">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{user?.name}</h1>
                <p className="text-sm text-gray-500">{user?.email}</p>
                <p className="text-xs text-gray-400 mt-0.5">Member since {new Date(user?.createdAt || '').toLocaleDateString('en-GY', { month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {activeOrders > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-xs font-semibold text-amber-700 whitespace-nowrap">{activeOrders} active</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap border border-gray-200 rounded-lg px-4 py-2 hover:border-red-200"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-logout-box-r-line text-base" />
                </div>
                Sign Out
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total Orders', value: orders.length, icon: 'ri-shopping-bag-3-line' },
              { label: 'Delivered', value: deliveredOrders, icon: 'ri-checkbox-circle-line' },
              { label: 'Total Spent', value: orders.length > 0 ? `GYD ${totalSpent.toLocaleString()}` : '—', icon: 'ri-wallet-3-line' },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-4 text-center">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] mx-auto mb-2">
                  <i className={`${s.icon} text-base`} />
                </div>
                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
            {(['orders', 'profile'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${tab === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                {t === 'orders' ? 'Order History' : 'My Profile'}
              </button>
            ))}
          </div>

          {/* Order History Tab */}
          {tab === 'orders' && (
            <div className="space-y-3">
              {orders.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                    <i className="ri-shopping-bag-3-line text-3xl" />
                  </div>
                  <p className="font-medium text-gray-600">No orders yet</p>
                  <p className="text-sm mt-1">Your order history will show up here.</p>
                  <Link to="/menu" className="inline-block mt-4 bg-[#FF6B35] text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#e85d2a] transition-colors cursor-pointer whitespace-nowrap">
                    Browse Menu
                  </Link>
                </div>
              ) : (
                orders.map((order) => (
                  <OrderCard key={order.id} order={order} onCancelRequest={setCancelTargetId} />
                ))
              )}
            </div>
          )}

          {/* Profile Tab */}
          {tab === 'profile' && (
            <div className="bg-white border border-gray-100 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-base font-semibold text-gray-900">Personal Information</h2>
                {!editMode && (
                  <button
                    onClick={() => setEditMode(true)}
                    className="text-sm text-[#FF6B35] font-medium hover:underline cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line text-sm" />
                    </div>
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Full Name', value: formName, setter: setFormName, placeholder: 'Your name', icon: 'ri-user-line' },
                  { label: 'Email', value: user?.email || '', setter: null, placeholder: '', icon: 'ri-mail-line', disabled: true },
                  { label: 'Phone Number', value: formPhone, setter: setFormPhone, placeholder: '+592-600-0000', icon: 'ri-phone-line' },
                  { label: 'Delivery Address', value: formAddress, setter: setFormAddress, placeholder: 'E.g. 45 Camp St, Georgetown', icon: 'ri-map-pin-line' },
                ].map((field) => (
                  <div key={field.label}>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">{field.label}</label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                        <i className={`${field.icon} text-base`} />
                      </div>
                      <input
                        type="text"
                        value={field.value}
                        onChange={field.setter ? (e) => field.setter!(e.target.value) : undefined}
                        placeholder={field.placeholder}
                        readOnly={!editMode || !field.setter}
                        className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm transition-all text-gray-800 ${
                          !editMode || !field.setter
                            ? 'bg-[#fafaf8] border-gray-100 text-gray-600 cursor-default'
                            : 'bg-white border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35]'
                        }`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {editMode && (
                <div className="flex gap-3 mt-6 pt-5 border-t border-gray-100">
                  <button
                    onClick={handleSaveProfile}
                    className="flex-1 bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-semibold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => { setEditMode(false); setFormName(user?.name || ''); setFormPhone(user?.phone || ''); setFormAddress(user?.address || ''); }}
                    className="px-5 border border-gray-200 text-gray-600 font-medium py-3 rounded-lg text-sm hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
